import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  Edit3, 
  Trash2, 
  X, 
  DollarSign, 
  CheckCircle2, 
  Briefcase, 
  Clock, 
  Mail, 
  Phone 
} from 'lucide-react';
import { CustomerRecord, CustomerType, CustomerCrmStage } from '@ftt/shared';
import { CUSTOMER_CRM_STAGES_CONFIG } from '@ftt/shared';

export const AdminCustomersPage: React.FC = () => {
  const [selectedType, setSelectedType] = useState<string>('ALL');
  const [selectedStage, setSelectedStage] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<CustomerRecord | null>(null);

  // Form State
  const [name, setName] = useState('');
  const [organization, setOrganization] = useState('');
  const [customerType, setCustomerType] = useState<CustomerType>('AUTHOR');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('India');
  const [city, setCity] = useState('');
  const [totalContractValue, setTotalContractValue] = useState(0);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);
  const [crmStage, setCrmStage] = useState<CustomerCrmStage>('IN_PRODUCTION');
  const [assignedStaffName, setAssignedStaffName] = useState('Jonathan D.');
  const [notes, setNotes] = useState('');

  const queryClient = useQueryClient();

  const { data: customersRes, isLoading } = useQuery({
    queryKey: ['admin-customers', { type: selectedType, stage: selectedStage, search: searchQuery }],
    queryFn: () => {
      let url = '/admin/customers';
      const params = new URLSearchParams();
      if (selectedType !== 'ALL') params.append('type', selectedType);
      if (selectedStage !== 'ALL') params.append('stage', selectedStage);
      if (searchQuery) params.append('search', searchQuery);
      if (params.toString()) url += `?${params.toString()}`;
      return api.get(url).then((res) => res.data.data);
    },
  });

  const customers: CustomerRecord[] = customersRes || [];

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name,
        organization: organization || null,
        customerType,
        email,
        phone: phone || null,
        country,
        city: city || null,
        totalContractValue,
        totalPaidAmount,
        crmStage,
        assignedStaffName,
        notes: notes || null,
      };

      if (editingCustomer?.id) {
        return api.put(`/admin/customers/${editingCustomer.id}`, payload);
      } else {
        return api.post('/admin/customers', payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-customers'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/customers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-customers'] });
    },
  });

  const resetForm = () => {
    setEditingCustomer(null);
    setName('');
    setOrganization('');
    setCustomerType('AUTHOR');
    setEmail('');
    setPhone('');
    setCountry('India');
    setCity('');
    setTotalContractValue(0);
    setTotalPaidAmount(0);
    setCrmStage('IN_PRODUCTION');
    setAssignedStaffName('Jonathan D.');
    setNotes('');
  };

  const handleOpenEdit = (customer: CustomerRecord) => {
    setEditingCustomer(customer);
    setName(customer.name);
    setOrganization(customer.organization || '');
    setCustomerType(customer.customerType);
    setEmail(customer.email);
    setPhone(customer.phone || '');
    setCountry(customer.country);
    setCity(customer.city || '');
    setTotalContractValue(customer.totalContractValue);
    setTotalPaidAmount(customer.totalPaidAmount);
    setCrmStage(customer.crmStage);
    setAssignedStaffName(customer.assignedStaffName || 'Jonathan D.');
    setNotes(customer.notes || '');
    setIsModalOpen(true);
  };

  // Metrics
  const totalContractRevenue = customers.reduce((sum, c) => sum + c.totalContractValue, 0);
  const totalPaidRevenue = customers.reduce((sum, c) => sum + c.totalPaidAmount, 0);
  const totalOutstanding = customers.reduce((sum, c) => sum + c.outstandingBalance, 0);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-brand-400 font-bold">
            CRM &amp; Author Pipeline
          </span>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-white tracking-tight">
            Customer &amp; Author Directory
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Track client relationships, publishing contracts, lifetime revenue, and outstanding milestone balances.
          </p>
        </div>

        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-bold text-xs shadow-md transition-all shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span>Add New Customer</span>
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 space-y-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold">Total Accounts</span>
          <div className="text-2xl font-bold text-white font-mono">{customers.length}</div>
          <span className="text-[10px] text-slate-500">Contracted authors &amp; ministries</span>
        </div>

        <div className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 space-y-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-brand-400 font-semibold">Contracted Value</span>
          <div className="text-2xl font-bold text-brand-400 font-mono">₹{totalContractRevenue.toLocaleString('en-IN')}</div>
          <span className="text-[10px] text-slate-500">Cumulative publishing fees</span>
        </div>

        <div className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 space-y-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400 font-semibold">Collected Cash</span>
          <div className="text-2xl font-bold text-emerald-400 font-mono">₹{totalPaidRevenue.toLocaleString('en-IN')}</div>
          <span className="text-[10px] text-slate-500">Paid to date</span>
        </div>

        <div className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 space-y-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-amber-400 font-semibold">Outstanding Receivables</span>
          <div className="text-2xl font-bold text-amber-400 font-mono">₹{totalOutstanding.toLocaleString('en-IN')}</div>
          <span className="text-[10px] text-slate-500">Pending milestone invoices</span>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search author, email or org..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-1.5 rounded-lg bg-slate-950 border border-slate-800 text-xs text-slate-200 placeholder-slate-500 focus:outline-hidden focus:border-brand-500 w-56 sm:w-64"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 font-medium">Stage:</span>
            <select
              value={selectedStage}
              onChange={(e) => setSelectedStage(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-slate-950 border border-slate-800 text-xs text-slate-200 focus:outline-hidden focus:border-brand-500"
            >
              <option value="ALL">All CRM Stages</option>
              <option value="IN_PRODUCTION">In Active Production</option>
              <option value="REPEAT_CLIENT">Repeat Author</option>
              <option value="CONTRACT_SIGNED">Contract Signed</option>
              <option value="OPPORTUNITY">Proposal</option>
              <option value="LEAD">Lead</option>
            </select>
          </div>
        </div>

        <div className="text-xs text-slate-400 font-mono">
          Showing <strong>{customers.length}</strong> clients
        </div>
      </div>

      {/* Customer List Table */}
      <div className="rounded-2xl bg-slate-950 border border-slate-800 overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-sans">
            <thead className="bg-slate-900/90 text-[10px] font-mono uppercase tracking-wider text-slate-400 border-b border-slate-800">
              <tr>
                <th className="py-3.5 px-4 font-semibold">Author / Client</th>
                <th className="py-3.5 px-4 font-semibold">Type &amp; Organization</th>
                <th className="py-3.5 px-4 font-semibold">CRM Lifecycle</th>
                <th className="py-3.5 px-4 font-semibold">Contract Value</th>
                <th className="py-3.5 px-4 font-semibold">Paid / Balance</th>
                <th className="py-3.5 px-4 font-semibold">Assigned Lead</th>
                <th className="py-3.5 px-4 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/80 text-slate-300">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-xs font-mono text-slate-500">
                    Loading customer records...
                  </td>
                </tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-xs text-slate-500">
                    No customers found matching filters.
                  </td>
                </tr>
              ) : (
                customers.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-900/50 transition-colors">
                    <td className="py-4 px-4 font-medium text-white">
                      <div>
                        <span className="font-serif font-bold text-sm block">{c.name}</span>
                        <span className="text-[11px] text-slate-400 font-mono">{c.email}</span>
                      </div>
                    </td>

                    <td className="py-4 px-4">
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-900 border border-slate-800 text-brand-300 font-semibold block w-fit mb-1">
                        {c.customerType}
                      </span>
                      <span className="text-xs text-slate-400">{c.organization || 'Independent'} &bull; {c.country}</span>
                    </td>

                    <td className="py-4 px-4">
                      <span className={`text-[10px] font-mono px-2.5 py-1 rounded-full font-bold ${
                        c.crmStage === 'IN_PRODUCTION'
                          ? 'bg-indigo-950 text-indigo-300 border border-indigo-800/40'
                          : c.crmStage === 'REPEAT_CLIENT'
                          ? 'bg-emerald-950 text-emerald-300 border border-emerald-800/40'
                          : 'bg-slate-900 text-slate-300 border border-slate-800'
                      }`}>
                        {c.crmStage.replace(/_/g, ' ')}
                      </span>
                    </td>

                    <td className="py-4 px-4 font-mono font-bold text-white">
                      ₹{c.totalContractValue.toLocaleString('en-IN')}
                    </td>

                    <td className="py-4 px-4 font-mono">
                      <div className="text-emerald-400 font-semibold">₹{c.totalPaidAmount.toLocaleString('en-IN')}</div>
                      {c.outstandingBalance > 0 ? (
                        <div className="text-amber-400 text-[10px]">₹{c.outstandingBalance.toLocaleString('en-IN')} pending</div>
                      ) : (
                        <div className="text-slate-500 text-[10px]">Settled</div>
                      )}
                    </td>

                    <td className="py-4 px-4 text-xs font-mono text-slate-400">
                      {c.assignedStaffName || 'Unassigned'}
                    </td>

                    <td className="py-4 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenEdit(c)}
                          className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white transition-colors"
                          title="Edit Customer"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`Remove customer "${c.name}"?`)) {
                              deleteMutation.mutate(c.id);
                            }
                          }}
                          className="p-1.5 rounded-lg bg-rose-950/40 hover:bg-rose-900/60 text-rose-400 transition-colors"
                          title="Delete Customer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Customer Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs overflow-y-auto">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-2xl w-full p-6 sm:p-8 space-y-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <h2 className="text-xl font-serif font-bold text-white">
                {editingCustomer ? 'Edit Customer / Author' : 'Add New Customer / Author'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveMutation.mutate();
              }}
              className="space-y-4 text-xs font-sans"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Dr. Arthur Pendleton"
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">Organization / Institution</label>
                  <input
                    type="text"
                    value={organization}
                    onChange={(e) => setOrganization(e.target.value)}
                    placeholder="e.g. Grace Seminary"
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="e.g. author@solagratia.org"
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">Phone Number</label>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 (555) 349-1188"
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">Customer Type</label>
                  <select
                    value={customerType}
                    onChange={(e) => setCustomerType(e.target.value as CustomerType)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  >
                    <option value="AUTHOR">Author</option>
                    <option value="MINISTRY_LEADER">Ministry Leader</option>
                    <option value="INSTITUTION_BUYER">Institutional Buyer</option>
                    <option value="TRANSLATOR">Translator</option>
                    <option value="SCHOLAR">Scholar / Academic</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">Country</label>
                  <input
                    type="text"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="e.g. India"
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">CRM Stage</label>
                  <select
                    value={crmStage}
                    onChange={(e) => setCrmStage(e.target.value as CustomerCrmStage)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  >
                    <option value="IN_PRODUCTION">In Production</option>
                    <option value="REPEAT_CLIENT">Repeat Client</option>
                    <option value="CONTRACT_SIGNED">Contract Signed</option>
                    <option value="OPPORTUNITY">Proposal Stage</option>
                    <option value="LEAD">Lead</option>
                    <option value="DORMANT">Dormant</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">Contract Total Value (INR ₹)</label>
                  <input
                    type="number"
                    value={totalContractValue}
                    onChange={(e) => setTotalContractValue(Number(e.target.value))}
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">Total Amount Paid (INR ₹)</label>
                  <input
                    type="number"
                    value={totalPaidAmount}
                    onChange={(e) => setTotalPaidAmount(Number(e.target.value))}
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-slate-300 font-semibold block">Assigned Editorial / Account Lead</label>
                <input
                  type="text"
                  value={assignedStaffName}
                  onChange={(e) => setAssignedStaffName(e.target.value)}
                  placeholder="e.g. Jonathan D."
                  className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-slate-300 font-semibold block">Internal Notes</label>
                <textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Author of theological monograph series."
                  className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                />
              </div>

              <div className="pt-4 border-t border-slate-800 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saveMutation.isPending}
                  className="px-6 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-xs font-bold shadow-md disabled:opacity-50"
                >
                  {saveMutation.isPending ? 'Saving...' : editingCustomer ? 'Update Customer' : 'Save Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
